import React from 'react';

import { useAsyncValue } from '@neptune/shared/common-util';
import { FilterResultItem, QueryFilterSearch } from '@neptune/shared/search-domain';

import { useDebounce } from 'common/hooks/useDebounce';

import { AddWithAutocompletion } from './AddWithAutocompletion';

type AddTagWithAutocompletionProps = {
  dataSource: QueryFilterSearch<string>;
  disabled?: boolean;
  placeholder?: string;
  onTagAdd: (tag: string) => void;
};

export const AddTagWithAutocompletion: React.FC<AddTagWithAutocompletionProps> = ({
  dataSource,
  disabled = false,
  placeholder = 'Enter value',
  onTagAdd,
}) => {
  const [fetchText, setFetchText] = React.useState('');
  const [searchValue, setSearchValue] = React.useState('');
  const [autocompletionValues, setAutocompletionValues] = React.useState<string[]>([]);

  const searchTextDebounced = useDebounce(fetchText, 200);
  const resolver = React.useCallback(
    () => dataSource(searchTextDebounced),
    [dataSource, searchTextDebounced],
  );
  const { value } = useAsyncValue({ resolver });

  const handleChange = React.useCallback((text: string) => {
    setSearchValue(text);

    if (!text) {
      setAutocompletionValues([]);
      setFetchText(text);
    }
  }, []);

  const handleFetchRequested = React.useCallback((text: string) => {
    setAutocompletionValues([]);
    setFetchText(text);
  }, []);

  const handleOnNewTag = React.useCallback(
    (tag: string) => {
      handleFetchRequested('');

      if (tag) {
        onTagAdd(tag);
      }
    },
    [onTagAdd, handleFetchRequested],
  );

  React.useEffect(() => {
    const filterResult = value?.entries ?? [];

    setAutocompletionValues(filterResult.map((entry: FilterResultItem<string>) => entry.original));
  }, [value?.entries]);

  return (
    <AddWithAutocompletion
      disabled={disabled}
      placeholder={placeholder}
      autocompletionValues={autocompletionValues}
      onSubmit={handleOnNewTag}
      onChange={handleChange}
      onFetchRequested={handleFetchRequested}
      value={searchValue}
    />
  );
};
